import { z } from 'zod';
import { ACCESS_LEVELS, ROLES } from '../constants';
import { terminologySchema } from '../terminology';

export const colorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use a hex color like #5B3FD6');
export const nameSchema = z.string().trim().min(1, 'Name is required').max(120);
export const positionSchema = z.string().min(1).max(64);

export const roleSchema = z.enum(ROLES);
export const accessLevelSchema = z.enum(ACCESS_LEVELS);
export const invitableRoleSchema = z.enum(['admin', 'member', 'guest']);

export const createWorkspaceSchema = z.object({
  name: nameSchema,
  businessType: z.string().max(32).optional(),
  terminology: terminologySchema.optional(),
});
export type CreateWorkspaceInput = z.infer<typeof createWorkspaceSchema>;

export const updateWorkspaceSchema = z.object({
  name: nameSchema.optional(),
  logo: z.string().max(500).nullable().optional(),
  settings: z.record(z.string(), z.unknown()).optional(),
});
export type UpdateWorkspaceInput = z.infer<typeof updateWorkspaceSchema>;

/** One place (space, folder or list) and the level of access to it. */
export const locationGrantSchema = z.object({
  resourceType: z.enum(['space', 'folder', 'list']),
  resourceId: z.string().max(64),
  level: accessLevelSchema,
});
export type LocationGrantInput = z.infer<typeof locationGrantSchema>;
export const locationGrantsSchema = z.array(locationGrantSchema).max(500);

export const createInviteSchema = z.object({
  email: z.email().transform((v) => v.toLowerCase()),
  role: invitableRoleSchema.default('member'),
  /** Members only: reach just the places below instead of everything that is not private. */
  limited: z.boolean().default(false),
  /** Places the person gets access to once they join. Guests and limited members see nothing else. */
  grants: locationGrantsSchema.optional(),
  /** Teams the person joins on accepting. */
  teamIds: z.array(z.string().max(64)).max(50).optional(),
  /** Whether those teams' places count for them. Off, they reach only their role's places and `grants`. */
  inheritTeamAccess: z.boolean().default(true),
});

/** Replaces everything a person or team has been given access to. */
export const setPrincipalAccessSchema = z.object({ grants: locationGrantsSchema });
export type SetPrincipalAccessInput = z.infer<typeof setPrincipalAccessSchema>;
export type CreateInviteInput = z.input<typeof createInviteSchema>;

export const updateMemberSchema = z.object({
  role: roleSchema.optional(),
  /** Members only: true restricts them to the places they have been given. */
  limited: z.boolean().optional(),
  /** Whether the places their teams can reach count for them. */
  inheritTeamAccess: z.boolean().optional(),
  title: z.string().max(120).nullable().optional(),
});
export type UpdateMemberInput = z.infer<typeof updateMemberSchema>;

export const createTeamSchema = z.object({
  name: nameSchema,
  color: colorSchema.optional(),
  memberIds: z.array(z.string()).max(200).optional(),
});
export const updateTeamSchema = createTeamSchema.partial();
export type CreateTeamInput = z.infer<typeof createTeamSchema>;
export type UpdateTeamInput = z.infer<typeof updateTeamSchema>;

export const createSpaceSchema = z.object({
  name: nameSchema,
  color: colorSchema.optional(),
  icon: z.string().max(16).nullable().optional(),
  isPrivate: z.boolean().optional(),
});
export const updateSpaceSchema = createSpaceSchema.partial().extend({
  position: positionSchema.optional(),
  settings: z.record(z.string(), z.unknown()).optional(),
});
export type CreateSpaceInput = z.infer<typeof createSpaceSchema>;
export type UpdateSpaceInput = z.infer<typeof updateSpaceSchema>;

export const createFolderSchema = z.object({
  spaceId: z.string(),
  name: nameSchema,
  icon: z.string().max(16).nullable().optional(),
  isPrivate: z.boolean().optional(),
});
export const updateFolderSchema = z.object({
  name: nameSchema.optional(),
  icon: z.string().max(16).nullable().optional(),
  isPrivate: z.boolean().optional(),
  spaceId: z.string().optional(),
  position: positionSchema.optional(),
});
export type CreateFolderInput = z.infer<typeof createFolderSchema>;
export type UpdateFolderInput = z.infer<typeof updateFolderSchema>;

export const createListSchema = z.object({
  spaceId: z.string(),
  folderId: z.string().nullable().optional(),
  name: nameSchema,
  color: colorSchema.nullable().optional(),
  icon: z.string().max(16).nullable().optional(),
  isPrivate: z.boolean().optional(),
  teamId: z.string().nullable().optional(),
});
export const updateListSchema = z.object({
  name: nameSchema.optional(),
  color: colorSchema.nullable().optional(),
  icon: z.string().max(16).nullable().optional(),
  isPrivate: z.boolean().optional(),
  spaceId: z.string().optional(),
  folderId: z.string().nullable().optional(),
  position: positionSchema.optional(),
  defaultView: z.string().max(32).nullable().optional(),
  teamId: z.string().nullable().optional(),
});
export type CreateListInput = z.infer<typeof createListSchema>;
export type UpdateListInput = z.infer<typeof updateListSchema>;

export const setGrantSchema = z.object({
  resourceType: z.string().max(32),
  resourceId: z.string(),
  principalType: z.enum(['user', 'team']),
  principalId: z.string(),
  /** null removes the grant. */
  level: accessLevelSchema.nullable(),
});
export type SetGrantInput = z.infer<typeof setGrantSchema>;
